import momentJalaali from 'moment-jalaali'

import { getTranslate, lang } from '../localization'

export type DateFullObject = {
  date: string
  month: string
  shortMonth: string
  monthDay: number
  weekDay: string
  year: string
}

export type DateItem = {
  gregorianDate: DateFullObject
  jalaaliDate: DateFullObject
}

export function getDatesList(
  perPage: number,
  from?: string,
  mode: 'day' | 'month' | '10-days' = 'day'
): DateItem[] {
  // If mode is 'month' or '10-days', start at the beginning of the month; otherwise, start at the specific date
  const startDate =
    mode === 'day' ? momentJalaali(from) : momentJalaali(from).startOf('month')
  const datesList: DateItem[] = []

  if (mode === 'month' && perPage === 12) {
    // Yearly view: Generate 12 months, each starting on the first day of the month
    for (let i = 0; i < perPage; i += 1) {
      const tmp = startDate.clone().add(i, 'months').startOf('month') // Ensure first day of each month
      datesList.push({
        gregorianDate: {
          date: tmp.format('YYYY-MM-DD'),
          month: tmp.format('MMMM'),
          shortMonth: tmp.format('MMM'),
          monthDay: tmp.date(),
          weekDay: tmp.format('ddd'),
          year: tmp.format('YYYY')
        },
        jalaaliDate: {
          date: tmp.format('jYYYY-jMM-jDD'),
          month: tmp.format('jMMMM'),
          shortMonth: tmp.format('jMMM'),
          monthDay: Number(tmp.format('jD')),
          weekDay: tmp.format('ddd'),
          year: tmp.format('jYYYY')
        }
      })
    }
  } else if (mode === '10-days' && perPage === 9) {
    // Three-Month View: Generate 10-day segments starting from the first day of each month
    for (let monthIndex = 0; monthIndex < 3; monthIndex += 1) {
      const monthStart = startDate
        .clone()
        .add(monthIndex, 'months')
        .startOf('month') // Start of each month
      for (let i = 0; i < 3; i += 1) {
        const tmp = monthStart.clone().add(i * 10, 'days') // Each 10-day segment
        datesList.push({
          gregorianDate: {
            date: tmp.format('YYYY-MM-DD'),
            month: tmp.format('MMMM'),
            shortMonth: tmp.format('MMM'),
            monthDay: tmp.date(),
            weekDay: tmp.format('ddd'),
            year: tmp.format('YYYY')
          },
          jalaaliDate: {
            date: tmp.format('jYYYY-jMM-jDD'),
            month: tmp.format('jMMMM'),
            shortMonth: tmp.format('jMMM'),
            monthDay: Number(tmp.format('jD')),
            weekDay: tmp.format('ddd'),
            year: tmp.format('jYYYY')
          }
        })
      }
    }
  } else {
    // Default Daily View (Monthly): Generate per day for a total of `perPage` days, starting on the exact selected date
    for (let i = 0; i < perPage; i += 1) {
      const tmp = startDate.clone().add(i, 'days')
      datesList.push({
        gregorianDate: {
          date: tmp.format('YYYY-MM-DD'),
          month: tmp.format('MMMM'),
          shortMonth: tmp.format('MMM'),
          monthDay: tmp.date(),
          weekDay: tmp.format('ddd'),
          year: tmp.format('YYYY')
        },
        jalaaliDate: {
          date: tmp.format('jYYYY-jMM-jDD'),
          month: tmp.format('jMMMM'),
          shortMonth: tmp.format('jMMM'),
          monthDay: Number(tmp.format('jD')),
          weekDay: tmp.format('ddd'),
          year: tmp.format('jYYYY')
        }
      })
    }
  }

  return datesList
}
export type DatePickerType = 'gregorian' | 'jalaali' | undefined

export type DatePickerSizeType = 'big' | 'small'

export type DateRangeType = 'END_DATE' | 'START_DATE'

export type DateObject = {
  gregorianStartDate?: string | undefined
  gregorianEndDate?: string | undefined
  jalaaliStartDate?: string | undefined
  jalaaliEndDate?: string | undefined
}

export function GregorianWithDetailsToGregorian(
  gregorianDateWithDetails: string
): any {
  return momentJalaali(gregorianDateWithDetails, 'YYYY-MM-DD HH:mm:ss').format(
    'YYYY-MM-DD'
  )
}

export function jalaaliToGregorian(jalaaliDate: string): any {
  return momentJalaali(jalaaliDate, 'jYYYY-jMM-jDD').format('YYYY-MM-DD')
}
export function jalaaliToGregorianWithHourMinuteAndSecond(
  jalaaliDateWithTime: string
): string {
  return momentJalaali(jalaaliDateWithTime, 'jYYYY-jMM-jDD HH:mm:ss').format(
    'YYYY-MM-DD HH:mm:ss'
  )
}

export function gregorianToJalaali(gregorianDate: string): string {
  return momentJalaali(gregorianDate, 'YYYY-MM-DD').format('jYYYY-jMM-jDD')
}

export function gregorianToJalaaliYear(gregorianDate: string): string {
  return momentJalaali(gregorianDate, 'YYYY').format('jYYYY')
}

export function gregorianToJalaaliWithHourMinuteAndSecond(
  gregorianDateWithTime: string
): string {
  return momentJalaali(gregorianDateWithTime, 'YYYY-MM-DD HH:mm:ss').format(
    'HH:mm:ss jYYYY-jMM-jDD'
  )
}

export function gregorianToJalaaliWithHourMinuteAndSecondAndMilisecond(
  gregorianDateWithTime: string
): string {
  return momentJalaali(gregorianDateWithTime, 'YYYY-MM-DD HH:mm:ss.SSS').format(
    'HH:mm:ss.SSS jYYYY-jMM-jDD'
  )
}

const jalaaliTimeView = {
  basicView: 'jYYYY-jMM-jDD',
  dayView: 'jDD',
  monthView: 'jMMMM',
  yearView: 'jYYYY'
}

const gregorianTimeView = {
  basicView: 'YYYY-MM-DD',
  dayView: 'DD',
  monthView: 'MMM',
  yearView: 'YYYY'
}

export function getTimeView(type: DatePickerType) {
  return type === 'jalaali' ? jalaaliTimeView : gregorianTimeView
}

export function getStartDateFromDateObject(
  dateObject: DateObject,
  type: DatePickerType
) {
  if (type === 'jalaali') {
    return dateObject.gregorianStartDate
      ? gregorianToJalaali(dateObject.gregorianStartDate)
      : dateObject.jalaaliStartDate
  }

  return dateObject.jalaaliStartDate
    ? jalaaliToGregorian(dateObject.jalaaliStartDate)
    : dateObject.gregorianStartDate
}

export function getEndDateFromDateObject(
  dateObject: DateObject,
  type: DatePickerType
) {
  if (type === 'jalaali') {
    return dateObject.gregorianEndDate
      ? gregorianToJalaali(dateObject.gregorianEndDate)
      : dateObject.jalaaliEndDate
  }
  return dateObject.jalaaliEndDate
    ? jalaaliToGregorian(dateObject.jalaaliEndDate)
    : dateObject.gregorianEndDate
}

export const formatDateFullObject = (item: DateItem): string => {
  return momentJalaali(item.gregorianDate.date, 'YYYY-MM-DD').format(
    lang === 'fa' ? 'jDD jMMMM jYYYY' : 'MMM D, YYYY'
  )
}

export const formatDateRange = (
  startDate: string,
  endDate: string,
  l: 'fa' | 'en' = 'en'
): string => {
  const start = momentJalaali(startDate, 'YYYY-MM-DD')
  const end = momentJalaali(endDate, 'YYYY-MM-DD')

  // Check if the dates are in the same month and year
  const sameMonth = start.isSame(end, 'month')
  const sameYear = start.isSame(end, 'year')

  if (l === 'fa') {
    if (sameMonth && sameYear) {
      return `${start.format('jD')} تا ${end.format('jD jMMMM jYYYY')}`
    }
    return `${start.format('jD jMMMM')} تا ${end.format('jD jMMMM jYYYY')}`
  } else {
    if (sameMonth && sameYear) {
      return `${start.format('D')} to ${end.format('D MMM, YYYY')}`
    }
    return `${start.format('D MMM')} to ${end.format('D MMM, YYYY')}`
  }
}

export const getTodayDate = (language: string): string => {
  if (language === 'fa') {
    return momentJalaali().format('jDD-jMM-jYYYY')
  } else {
    return momentJalaali().format('YYYY-MM-DD')
  }
}

export const addDaysToDate = (dateString: string, days: number): string => {
  return momentJalaali(dateString, 'YYYY-MM-DD')
    .add(days, 'days')
    .format('YYYY-MM-DD')
}

export const isWeekend = (item: DateItem): boolean => {
  return ['Thu', 'Fri', 'پنج‌شنبه', 'جمعه'].includes(item.gregorianDate.weekDay)
}

export function DeleteExtraDetailsFromDate(datetimeStr: string): string {
  return datetimeStr.slice(0, 10)
}

export type DatePickerSelectionType = 'TimeInterval' | 'CustomDays'

export const isValidDateFormat = (date: string | null): boolean => {
  const dateRegex = /^(?:(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))?$/
  return dateRegex.test(date ?? '')
}

export const formatRange = (from: string, to: string) => {
  const startDate = momentJalaali(from, 'YYYY-MM-DD')
  const endDate = momentJalaali(to, 'YYYY-MM-DD')

  // Choose format based on language
  const monthDayFormat = lang === 'fa' ? 'jD jMMM' : 'D MMM'

  // Format dates accordingly
  const formattedFrom = startDate.format(monthDayFormat)
  const formattedTo = endDate.format(monthDayFormat)

  return `${formattedFrom} ${getTranslate('to')} ${formattedTo}`
}

export const formatShortDateWithWeekDay = (
  date: string,
  locale: 'fa' | 'en' = 'en'
): string => {
  const format = locale === 'fa' ? 'ddd، jD jMMMM' : 'ddd, MMMM D'
  return momentJalaali(date, 'YYYY-MM-DD').format(format)
}

export const formatTimeLeft = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hrs.toString().padStart(2, '0')}:${mins
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

export function toISO8601(date: string, time: string): string {
  // Ensure time has seconds
  const timeWithSeconds = time.length === 5 ? `${time}:00` : time

  // Combine into ISO format
  return `${date}T${timeWithSeconds}Z`
}

export function splitDateTime(datetime: string): {
  date: string
  time: string
} {
  if (datetime === '') {
    return { date: '2025-09-28', time: '10:00' }
  }

  const [date, time] = datetime.split(' ')
  const shortTime = time.slice(0, 5) // keeps only "HH:mm"
  return { date, time: shortTime }
}

export function combineDateTime(date: string, time: string): string {
  return `${date} ${time}`
}
